import { useEffect, useMemo, useState } from 'react';
import {
  Users, Search, ShieldCheck, Mail, Calendar, CreditCard,
  Zap, ChevronRight, Sparkles, X, ExternalLink, Bot,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AdminAPI } from '@/lib/admin-api';
import { formatDateTime, formatDistanceToNow } from '@/lib/format-time';
import { cn } from '@/lib/utils';

/**
 * Admin · Customer base
 * ---------------------
 * Read-only list of every signed-up user with per-user aggregates
 * (cards owned, recommend queries, gmail-linked flag, last activity).
 * Row click opens a right-hand drawer with cards owned, recent
 * recommendations, and Gmail connection metadata.
 */

const AUTH_BADGE = {
  google: 'bg-primary/10 text-primary border-primary/30',
  password: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
};

const GOAL_LABEL = {
  travel: 'Travel',
  cashback: 'Cashback',
  points: 'Points',
  luxury: 'Luxury',
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-lg border border-border bg-[hsl(var(--surface-2))] px-3 py-2" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className="mt-0.5 font-display text-lg font-semibold tabular-nums">{value}</div>
  </div>
);

const Avatar = ({ src, name, className }) => {
  const initials = (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
  return src ? (
    <img
      src={src}
      alt={name}
      className={cn('rounded-full object-cover', className)}
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
      data-testid="admin-user-avatar-img"
    />
  ) : (
    <div
      className={cn('rounded-full grid place-items-center bg-primary/10 text-primary text-[11px] font-semibold', className)}
      data-testid="admin-user-avatar-initials"
    >
      {initials || '?'}
    </div>
  );
};

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [authFilter, setAuthFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    AdminAPI.users.list({
      role: roleFilter,
      auth: authFilter,
      limit: 500,
    })
      .then((data) => {
        if (cancelled) return;
        setRows(data.users || []);
        setTotal(data.total || 0);
      })
      .catch(() => { if (!cancelled) { setRows([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [roleFilter, authFilter]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const qq = q.toLowerCase();
    return rows.filter((u) =>
      (u.name || '').toLowerCase().includes(qq) ||
      (u.email || '').toLowerCase().includes(qq) ||
      (u.id || '').toLowerCase().includes(qq),
    );
  }, [rows, q]);

  const summary = useMemo(() => {
    const totalCards = rows.reduce((s, u) => s + (u.cards_count || 0), 0);
    const totalRecs = rows.reduce((s, u) => s + (u.recs_count || 0), 0);
    const gmailLinked = rows.filter((u) => u.gmail_linked).length;
    const admins = rows.filter((u) => u.is_admin).length;
    return { totalCards, totalRecs, gmailLinked, admins };
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="admin-users-page">
      {/* Page header */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">Operations</div>
        <h1 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">Customers</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          Every signed-up user. Read-only — for account changes, use auth or Gmail settings on the customer side.
        </p>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <Stat icon={Users} label="Total users" value={total} testid="admin-users-kpi-total" />
        <Stat icon={CreditCard} label="Cards linked" value={summary.totalCards} testid="admin-users-kpi-cards" />
        <Stat icon={Sparkles} label="Recommend queries" value={summary.totalRecs.toLocaleString('en-IN')} testid="admin-users-kpi-recs" />
        <Stat icon={Mail} label="Gmail-linked" value={summary.gmailLinked} testid="admin-users-kpi-gmail" />
      </div>

      {/* Filter bar */}
      <Card className="p-3 flex flex-col sm:flex-row gap-3" data-testid="admin-users-filter-bar">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter by name, email, or user id..."
            className="h-9 pl-8 border-transparent bg-[hsl(var(--surface-2))]"
            data-testid="admin-users-search-input"
          />
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-9 w-full sm:w-[140px]" data-testid="admin-users-filter-role">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
              <SelectItem value="user">Customers</SelectItem>
            </SelectContent>
          </Select>
          <Select value={authFilter} onValueChange={setAuthFilter}>
            <SelectTrigger className="h-9 w-full sm:w-[140px]" data-testid="admin-users-filter-auth">
              <SelectValue placeholder="Auth" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All auth</SelectItem>
              <SelectItem value="google">Google</SelectItem>
              <SelectItem value="password">Password</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground" data-testid="admin-users-empty-state">
          <Users className="h-6 w-6 mx-auto opacity-50 mb-2" />
          <div className="text-sm">No users match this filter.</div>
        </Card>
      ) : (
        <Card className="divide-y divide-border/40" data-testid="admin-users-table">
          {/* Column headers — desktop only */}
          <div className="hidden md:grid grid-cols-[minmax(0,2fr)_120px_120px_100px_100px_130px_20px] gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-muted-foreground bg-[hsl(var(--surface-2))]/50">
            <div>User</div>
            <div>Signup</div>
            <div>Last active</div>
            <div className="text-right">Cards</div>
            <div className="text-right">Queries</div>
            <div>Gmail</div>
            <div />
          </div>
          {filtered.map((u) => (
            <button
              key={u.id}
              onClick={() => setSelectedId(u.id)}
              className="w-full text-left px-4 py-3 hover:bg-[hsl(var(--surface-2))]/70 transition-colors grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_120px_120px_100px_100px_130px_20px] gap-3 items-center"
              data-testid={`admin-users-row-${u.id}`}
            >
              {/* User cell */}
              <div className="flex items-center gap-3 min-w-0">
                <Avatar src={u.picture} name={u.name} className="h-9 w-9" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-medium text-sm truncate">{u.name || '—'}</span>
                    {u.is_admin && (
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wide h-4 px-1.5 border-accent/40 text-accent bg-accent/5" data-testid={`admin-users-badge-admin-${u.id}`}>
                        Admin
                      </Badge>
                    )}
                    {u.is_demo && (
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wide h-4 px-1.5 border-muted-foreground/30 text-muted-foreground">
                        Demo
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground truncate">
                    <span className="truncate">{u.email}</span>
                    <Badge variant="outline" className={cn('shrink-0 text-[9px] uppercase tracking-wide h-4 px-1.5', AUTH_BADGE[u.auth_provider] || '')}>
                      {u.auth_provider}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Signup */}
              <div className="text-xs">
                <div className="text-foreground">{(u.signup_at || '').slice(0, 10) || '—'}</div>
                <div className="text-[10px] text-muted-foreground">{u.signup_at ? formatDistanceToNow(u.signup_at) : ''}</div>
              </div>

              {/* Last active */}
              <div className="text-xs">
                {u.last_activity_at ? (
                  <>
                    <div className="text-foreground">{formatDistanceToNow(u.last_activity_at)}</div>
                    <div className="text-[10px] text-muted-foreground">{u.last_activity_at.slice(0, 10)}</div>
                  </>
                ) : (
                  <span className="text-muted-foreground">Never</span>
                )}
              </div>

              {/* Cards count */}
              <div className="text-right tabular-nums text-sm font-medium">
                {u.cards_count || 0}
              </div>

              {/* Recs count */}
              <div className="text-right tabular-nums text-sm font-medium">
                {(u.recs_count || 0).toLocaleString('en-IN')}
              </div>

              {/* Gmail */}
              <div>
                {u.gmail_linked ? (
                  <Badge variant="outline" className="text-[10px] gap-1 border-emerald-500/30 text-emerald-600 bg-emerald-500/5" data-testid={`admin-users-gmail-linked-${u.id}`}>
                    <Mail className="h-3 w-3" /> Linked
                  </Badge>
                ) : (
                  <span className="text-[10px] text-muted-foreground">—</span>
                )}
              </div>

              <ChevronRight className="hidden md:block h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </Card>
      )}

      {/* Detail drawer */}
      <UserDetailDrawer
        userId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────── //
// Detail drawer
// ─────────────────────────────────────────────────────────────────────── //
const UserDetailDrawer = ({ userId, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    AdminAPI.users.get(userId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const u = data?.user;
  const s = data?.stats || {};
  const cards = data?.cards || [];
  const recs = data?.recent_recs || [];
  const gmail = data?.gmail;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0" data-testid="admin-user-detail-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-start justify-between gap-2">
            <SheetTitle className="text-base">Customer</SheetTitle>
            <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7" data-testid="admin-user-detail-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        {loading || !u ? (
          <div className="p-5 space-y-3">
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Header card: avatar + name + badges */}
            <div className="flex items-start gap-3">
              <Avatar src={u.picture} name={u.name} className="h-14 w-14" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div className="font-display text-lg font-semibold truncate" data-testid="admin-user-detail-name">{u.name}</div>
                  {u.is_admin && <Badge variant="outline" className="text-[9px] uppercase tracking-wide h-4 px-1.5 border-accent/40 text-accent bg-accent/5">Admin</Badge>}
                  {u.is_demo && <Badge variant="outline" className="text-[9px] uppercase tracking-wide h-4 px-1.5">Demo</Badge>}
                </div>
                <a
                  href={`mailto:${u.email}`}
                  className="text-sm text-primary hover:underline break-all inline-flex items-center gap-1"
                  data-testid="admin-user-detail-email"
                >
                  {u.email} <ExternalLink className="h-3 w-3" />
                </a>
                <div className="mt-1 flex items-center gap-3 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" />
                    {u.auth_provider === 'google' ? 'Google OAuth' : 'Password'}
                  </span>
                  {u.goal && (
                    <span className="inline-flex items-center gap-1">
                      <Zap className="h-3 w-3" />
                      {GOAL_LABEL[u.goal] || u.goal}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Stat quad */}
            <div className="grid grid-cols-4 gap-2">
              <Stat icon={CreditCard} label="Cards" value={s.cards_count} testid="admin-user-detail-stat-cards" />
              <Stat icon={Sparkles} label="Recommend" value={(s.recs_count || 0).toLocaleString('en-IN')} testid="admin-user-detail-stat-recs" />
              <Stat icon={Bot} label="Tx logged" value={s.tx_count || 0} testid="admin-user-detail-stat-tx" />
              <Stat icon={Mail} label="Gmail" value={s.gmail_linked ? 'Yes' : 'No'} testid="admin-user-detail-stat-gmail" />
            </div>

            {/* Timeline */}
            <div className="rounded-xl border border-border p-3 space-y-1.5 text-xs" data-testid="admin-user-detail-timeline">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Timeline</div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground inline-flex items-center gap-1.5"><Calendar className="h-3 w-3" /> Signed up</span>
                <span className="tabular-nums">{u.signup_at ? formatDateTime(u.signup_at) : '—'}</span>
              </div>
              {u.last_login_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground inline-flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> Last login</span>
                  <span className="tabular-nums">{formatDateTime(u.last_login_at)}</span>
                </div>
              )}
              {u.last_auto_sync_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground inline-flex items-center gap-1.5"><Mail className="h-3 w-3" /> Last Gmail sync</span>
                  <span className="tabular-nums">{formatDateTime(u.last_auto_sync_at)}</span>
                </div>
              )}
            </div>

            {/* Gmail block */}
            {gmail && (
              <div className="rounded-xl border border-border p-3 space-y-1.5 text-xs" data-testid="admin-user-detail-gmail">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1 flex items-center gap-1.5">
                  <Mail className="h-3 w-3" /> Gmail connection
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Gmail account</span>
                  <span className="truncate max-w-[220px]">{gmail.gmail_address || '—'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Connected</span>
                  <span className="tabular-nums">{gmail.connected_at ? formatDateTime(gmail.connected_at) : '—'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Last sync</span>
                  <span className="tabular-nums">{gmail.last_sync_at ? formatDateTime(gmail.last_sync_at) : '—'}</span>
                </div>
                {gmail.last_sync_status && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Sync status</span>
                    <span className="text-emerald-600">
                      {/* last_sync_status may be a string (legacy) OR an object
                          like { label: 'ok', drafts: 6 } (new shape). Render
                          the label when present, else stringify defensively. */}
                      {typeof gmail.last_sync_status === 'string'
                        ? gmail.last_sync_status
                        : (gmail.last_sync_status.label || JSON.stringify(gmail.last_sync_status))}
                      {typeof gmail.last_sync_status === 'object'
                        && gmail.last_sync_status?.drafts != null
                        && <span className="text-muted-foreground"> · {gmail.last_sync_status.drafts} drafts</span>}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Cards owned */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <CreditCard className="h-3 w-3" /> Cards owned ({cards.length})
              </div>
              {cards.length === 0 ? (
                <div className="text-xs text-muted-foreground">No cards added yet.</div>
              ) : (
                <div className="space-y-1.5" data-testid="admin-user-detail-cards-list">
                  {cards.map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-xs">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{c.card_name || c.card_id}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {[c.card_bank, c.network].filter(Boolean).join(' · ') || '—'}
                          {c.nickname && <> · {c.nickname}</>}
                          {c.last4 && <> · ····{c.last4}</>}
                        </div>
                      </div>
                      <div className="text-right text-[10px] text-muted-foreground shrink-0">
                        {c.added_at ? formatDistanceToNow(c.added_at) : ''}
                        {c.current_reward_points ? (
                          <div className="text-primary font-medium">
                            {c.current_reward_points.toLocaleString('en-IN')} RP
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent recommendations */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2 flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" /> Recent Recommend queries ({recs.length}
                {s.recs_count > recs.length && <span> of {s.recs_count.toLocaleString('en-IN')}</span>})
              </div>
              {recs.length === 0 ? (
                <div className="text-xs text-muted-foreground">This user hasn&rsquo;t run any Recommend queries yet.</div>
              ) : (
                <div className="space-y-1.5" data-testid="admin-user-detail-recs-list">
                  {recs.map((r) => (
                    <div key={r.id} className="rounded-lg border border-border px-3 py-2 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 truncate">
                          <span className="font-medium">{r.category || 'General'}</span>
                          {r.merchant && <span className="text-muted-foreground"> · {r.merchant}</span>}
                          {r.amount ? <span className="text-muted-foreground"> · ₹{Number(r.amount).toLocaleString('en-IN')}</span> : null}
                        </div>
                        <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
                          {r.created_at ? formatDistanceToNow(r.created_at) : ''}
                        </span>
                      </div>
                      {(r.mode || r.international) && (
                        <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          {r.mode && <span className="capitalize">{r.mode}</span>}
                          {r.international && <span>· International</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
